import { type ComponentType, type ReactNode, useState } from 'react';
import { cn } from '@hero/web-lib';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/Tabs';

/**
 * Icon component shape every tab can mount in the trigger row.
 * Compatible with any `@hero/web-icons` icon (they all accept a
 * `className` prop) — but loose enough to also accept custom SVG
 * wrappers that take `className`.
 */
export type IconTabIcon = ComponentType<{ className?: string }>;

/**
 * One tab descriptor consumed by {@link IconTabs}. The generic
 * `TId` lets call-sites narrow the union of valid ids so the
 * controlled `value` / `onValueChange` / `defaultValue` props
 * stay type-safe end-to-end.
 *
 * Example:
 *   ```ts
 *   type ProfileTab = 'general' | 'legal' | 'banking';
 *   const tabs: IconTab<ProfileTab>[] = [
 *     { id: 'general', title: t('tabs.general'), icon: BuildingIcon, content: <GeneralTab /> },
 *     // …
 *   ];
 *   ```
 */
export interface IconTab<TId extends string = string> {
  /** Stable id. Pair with `value` / `onValueChange` for controlled tab state. */
  id: TId;
  /** Already-translated label shown next to the icon. */
  title: string;
  /**
   * Icon component rendered before the title. Pass any icon from
   * `@hero/web-icons` directly — its `className` prop drives the
   * size + color (defaults to `size-4 text-current`).
   */
  icon?: IconTabIcon;
  /** Tab body. Rendered inside `<TabsContent>`. */
  content: ReactNode;
  /** Disable this tab's trigger. */
  disabled?: boolean;
}

export interface IconTabsProps<TId extends string = string> {
  /** Ordered list of tabs to render. The first tab is the default if
   *  no `value` / `defaultValue` is provided. */
  tabs: IconTab<TId>[];
  /** Controlled active tab id. Pair with `onValueChange`. */
  value?: TId;
  /** Initial active tab id (uncontrolled). Falls back to `tabs[0].id`. */
  defaultValue?: TId;
  /** Fires whenever the user activates a different tab. */
  onValueChange?: (value: TId) => void;
  /** Pass-through className for the outer `<Tabs>` root. */
  className?: string;
  /** Pass-through className for the `<TabsList>` (trigger row). */
  listClassName?: string;
  /** Pass-through className applied to each `<TabsContent>` panel. */
  contentClassName?: string;
}

/**
 * `IconTabs` — config-driven wrapper around the shared `Tabs`
 * primitive that renders an icon next to each trigger's label.
 *
 * Designed to keep tabbed screens (Org Profile, future Settings,
 * Driver / Vehicle detail pages, …) free of trigger boilerplate:
 * consumers pass a list of `{ id, title, icon, content }` and the
 * component handles RTL, controlled / uncontrolled state, and the
 * matching `<TabsContent>` mapping in one place. Renaming a tab
 * label or swapping an icon never touches the page-level code.
 *
 * Why a wrapper instead of inlining the Radix tabs every time?
 *  - Single place to standardise the trigger layout (icon + label
 *    spacing, RTL flipping) so different screens don't drift.
 *  - The id ↔ content mapping is implicit, so adding a tab is a
 *    one-line push to the config array; consumers don't have to
 *    keep two arrays (triggers + panels) in sync.
 *  - Generic over `TId` so the controlled API stays type-safe;
 *    you can't accidentally `onValueChange('xyz')` if your tab
 *    union is `'general' | 'legal'`.
 */
export function IconTabs<TId extends string = string>({
  tabs,
  value,
  defaultValue,
  onValueChange,
  className,
  listClassName,
  contentClassName,
}: IconTabsProps<TId>) {
  // Internal state used when the caller doesn't pass `value`. We
  // pick the first tab id so the panel is never empty at first
  // paint.
  const [internalValue, setInternalValue] = useState<TId>(
    defaultValue ?? tabs[0]?.id ?? ('' as TId),
  );
  const currentValue = value ?? internalValue;

  const handleChange = (next: string) => {
    const nextId = next as TId;
    if (value === undefined) setInternalValue(nextId);
    onValueChange?.(nextId);
  };

  return (
    <Tabs
      value={currentValue}
      onValueChange={handleChange}
      className={cn('w-full', className)}>
      <TabsList className={cn('mb-6', listClassName)}>
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <TabsTrigger
              key={tab.id}
              value={tab.id}
              disabled={tab.disabled}
              className="flex items-center gap-2">
              {Icon ? <Icon className="size-4 text-current" /> : null}
              <span>{tab.title}</span>
            </TabsTrigger>
          );
        })}
      </TabsList>

      {tabs.map(tab => (
        <TabsContent
          key={tab.id}
          value={tab.id}
          className={cn('space-y-6', contentClassName)}>
          {tab.content}
        </TabsContent>
      ))}
    </Tabs>
  );
}
